import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getMyOrders } from "@/lib/orders.functions";

export const Route = createFileRoute("/_authenticated/narocila")({
  head: () => ({ meta: [{ title: "Moja naročila — kupish.si" }] }),
  component: Page,
});

const STATUS_LABEL: Record<string, string> = {
  quote_requested: "Povpraševanje poslano",
  quote_sent: "Predračun prejet",
  pending: "V obdelavi",
  paid: "Plačano",
  shipped: "Odposlano",
  delivered: "Dostavljeno",
  canceled: "Preklicano",
  refunded: "Povrnjeno",
  failed: "Neuspelo",
};

function formatShippingAddress(addr: {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  country?: string;
  phone?: string;
} | null): string {
  if (!addr) return "";
  const parts = [
    addr.name,
    addr.line1,
    addr.line2,
    [addr.postal_code, addr.city].filter(Boolean).join(" "),
    addr.state,
    addr.country,
    addr.phone,
  ].filter(Boolean);
  return parts.join(", ");
}

function Page() {
  const fetchOrders = useServerFn(getMyOrders);
  const { data, isLoading } = useQuery({ queryKey: ["my-orders"], queryFn: () => fetchOrders() });

  return (
    <section className="mx-auto max-w-5xl px-6 py-16">
      <Link to="/dashboard" className="text-xs eyebrow hover:text-accent">← Nadzorna plošča</Link>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">Moja naročila</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Naročila potekajo po predračunu: prodajalec vam izda predračun, ki ga plačate po UPN.
        Po prejetem plačilu vam prodajalec izda račun in odpošlje paket.
      </p>

      {isLoading ? (
        <div className="mt-12 text-muted-foreground">Nalagam…</div>
      ) : !data || data.orders.length === 0 ? (
        <div className="mt-12 border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground">Še nimate naročil.</p>
          <Link to="/izdelki" className="mt-4 inline-block underline underline-offset-4">Razišči izdelke →</Link>
        </div>
      ) : (
        <div className="mt-10 space-y-8">
          {(() => {
            // Group orders by quote_group_id so multi-vendor carts appear as one card
            const groups = new Map<string, typeof data.orders>();
            for (const o of data.orders) {
              const key = o.quote_group_id ?? `single:${o.id}`;
              const arr = groups.get(key) ?? [];
              arr.push(o);
              groups.set(key, arr);
            }
            return Array.from(groups.entries()).map(([key, group]) => {
              const isGroup = group.length > 1;
              const groupTotal = group.reduce((s, o) => s + Number(o.amount_total), 0);
              const groupId = isGroup ? group[0].quote_group_id : null;
              const groupCreated = group[0].created_at;
              return (
                <div key={key} className="border border-border">
                  {isGroup && (
                    <div className="flex items-center justify-between flex-wrap gap-3 p-4 bg-accent/5 border-b border-border">
                      <div>
                        <div className="text-xs eyebrow text-accent">Skupinsko povpraševanje · {group.length} prodajalcev</div>
                        <div className="text-sm">{new Date(groupCreated).toLocaleDateString("sl-SI", { day: "numeric", month: "long", year: "numeric" })}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="font-serif text-lg text-accent">€{groupTotal.toFixed(2)}</div>
                        {groupId && (
                          <Link to="/povprasevanje/$groupId" params={{ groupId }} className="text-xs underline underline-offset-4 hover:text-accent">
                            Skupni pregled →
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                  <div className={isGroup ? "divide-y divide-border" : ""}>
                    {group.map((o) => (
                      <div key={o.id}>
                        <div className="flex items-center justify-between flex-wrap gap-3 p-5 bg-muted/30 border-b border-border">
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {isGroup ? <>Del naročila #{o.wp_order_number ?? o.id.slice(0, 8)}</> : <>Naročilo #{o.wp_order_number ?? o.id.slice(0, 8)}</>}
                              {o.invoice_number && <> · Račun {o.invoice_number}</>}
                            </div>
                            <div className="text-sm">{new Date(o.created_at).toLocaleDateString("sl-SI", { day: "numeric", month: "long", year: "numeric" })}</div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs uppercase tracking-wider px-2 py-1 bg-background border border-border">
                              {STATUS_LABEL[o.status] ?? o.status}
                            </span>
                            <div className="font-serif text-lg text-accent">€{o.amount_total.toFixed(2)}</div>
                          </div>
                        </div>
                        <div className="divide-y divide-border">
                          {o.items.map((it) => (
                            <div key={it.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium truncate">{it.title}</div>
                                {it.vendor_name && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    Prodajalec:{" "}
                                    {it.vendor_slug ? (
                                      <Link
                                        to="/prodajalci/$slug"
                                        params={{ slug: it.vendor_slug }}
                                        className="underline underline-offset-4 hover:text-accent"
                                      >
                                        {it.vendor_name}
                                      </Link>
                                    ) : (
                                      <span>{it.vendor_name}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-4 sm:text-right">
                                <div className="text-muted-foreground">{it.quantity}× <span className="text-accent">€{it.unit_price.toFixed(2)}</span></div>
                                <div className="font-serif text-accent min-w-[4.5rem]">€{it.line_total.toFixed(2)}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="p-4 border-t border-border bg-muted/20 text-sm">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Naročnik</div>
                              <div>{o.customer_name || "—"}</div>
                              <div className="text-muted-foreground text-xs">{o.customer_email || "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Naslov za dostavo</div>
                              {formatShippingAddress(o.shipping_address) ? (
                                <div className="text-xs text-muted-foreground leading-relaxed">
                                  {formatShippingAddress(o.shipping_address)}
                                </div>
                              ) : (
                                <div className="text-xs text-muted-foreground">—</div>
                              )}
                            </div>
                          </div>
                        </div>

                        {(o.quote_id || o.quote_pdf_url || o.invoice_pdf_url || o.tracking_number) && (
                          <div className="p-4 border-t border-border flex flex-wrap gap-4 text-xs">
                            {o.quote_id && (
                              <Link to="/predracun/$id" params={{ id: o.quote_id }} className="underline underline-offset-4 hover:text-accent">
                                Pogled predračuna →
                              </Link>
                            )}
                            {o.quote_pdf_url && (
                              <a href={o.quote_pdf_url} target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-accent">PDF predračun</a>
                            )}
                            {o.invoice_pdf_url && (
                              <a href={o.invoice_pdf_url} target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-accent">PDF račun</a>
                            )}
                            {o.tracking_number && (
                              <span className="text-muted-foreground">
                                Sledenje: {o.tracking_carrier && <>{o.tracking_carrier} · </>}
                                <span className="font-mono">{o.tracking_number}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </section>
  );
}
